import { useMemo, useState } from 'react';
import type {
  PurchaseLinesImportOptions,
  PurchaseLinesImportPreview,
} from '@obraplan/core/application/contracts/purchase-lines';
import { isProvisionalMaterialCode } from '@obraplan/core/domain/schedule/material';
import type { PurchaseLineRowData } from '@obraplan/core/domain/schedule/purchase-template/index';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { formatBusinessDate } from '@/lib/format';
import { STATUS_TONE, VISIBLE_LINES, listed, type StatusFilter } from './conferencia-model';
import { Option, PreviewStatus, ReaderIssues, StatusFilters, Warnings } from './conferencia-parts';
import {
  LINE_STATUS_LABEL,
  countLines,
  formatPurchaseQuantity,
  plural,
  summarizeSkipped,
  type JoinedLine,
  type LoadedLines,
} from './purchase-model';

export interface PurchaseConferenciaProps {
  readonly fileName: string;
  /** O que a leitura da aba "Itens de compra" produziu. */
  readonly lines: LoadedLines;
  /** A prévia do servidor; `undefined` enquanto ela não chega. */
  readonly preview: PurchaseLinesImportPreview | undefined;
  /** As linhas enviadas, já juntas à resposta. */
  readonly joined: readonly JoinedLine<PurchaseLineRowData>[] | undefined;
  readonly isPreviewing: boolean;
  readonly previewError: string | undefined;
  readonly options: PurchaseLinesImportOptions;
  readonly onOptions: (next: PurchaseLinesImportOptions) => void;
  /** Criar serviço exige `takeoff.manage` além de `schedule.import`. */
  readonly canCreateServices: boolean;
  /** Criar material exige `materials.manage`: o catálogo de material é global. */
  readonly canCreateMaterials: boolean;
}

/**
 * O passo de conferência das linhas de compra: **nada é gravado até a confirmação**.
 *
 * Mostra, nesta ordem, o que a leitura fez com a planilha (as linhas que entram, as que se
 * somaram e as que ficam só na planilha, com o motivo), o que falta no cadastro da obra
 * (com as cinco opções de criar, todas desligadas por padrão, RN-035), os avisos de que a
 * soma de um material não bate com a planilha (RN-350), a contagem do que a importação faria
 * e cada linha com o motivo, quando recusada.
 */
export function PurchaseConferencia(props: PurchaseConferenciaProps) {
  const { lines, preview, joined } = props;
  const [filter, setFilter] = useState<StatusFilter>('ALL');

  const counts = useMemo(() => (joined === undefined ? undefined : countLines(joined)), [joined]);
  const matching = useMemo(
    () =>
      joined === undefined
        ? []
        : filter === 'ALL'
          ? joined
          : joined.filter((line) => line.status === filter),
    [joined, filter],
  );

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm">
        <strong>{props.fileName}</strong>{' '}
        <span className="text-(--color-content-muted)">
          · aba &ldquo;{lines.sheetName}&rdquo; → {plural(lines.rows.length, 'linha', 'linhas')} de
          compra
        </span>
      </p>

      <Leitura lines={lines} />

      <ReaderIssues
        id="conf-lines-issues"
        title="Linhas da planilha que não puderam ser lidas"
        issues={lines.issues}
      />

      {preview !== undefined && <Cadastro {...props} preview={preview} />}

      {preview !== undefined && (
        <Warnings
          id="conf-lines-warnings"
          title="A soma por material não confere com a planilha"
          warnings={preview.warnings}
        />
      )}

      {counts === undefined || joined === undefined ? (
        <PreviewStatus previewError={props.previewError} isPreviewing={props.isPreviewing} />
      ) : (
        <section aria-labelledby="conf-lines-table" className="flex flex-col gap-3">
          <h3 id="conf-lines-table" className="sr-only">
            Linhas da importação
          </h3>
          {props.previewError !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {props.previewError}
            </p>
          )}
          <StatusFilters
            filter={filter}
            onFilter={setFilter}
            counts={counts}
            total={joined.length}
          />

          {matching.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">Nenhuma linha neste filtro.</p>
          ) : (
            <TableWrapper className="max-h-72 overflow-y-auto">
              <Table className="min-w-[56rem]">
                <TableHeader sticky>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-14">Linha</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead>Pavimento</TableHead>
                    <TableHead>Disciplina</TableHead>
                    <TableHead>Serviço</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead>Necessário em</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matching.slice(0, VISIBLE_LINES).map((line) => (
                    <TableRow key={line.index}>
                      <TableCell className="tabular text-(--color-content-muted)">
                        {line.row.rowNumber}
                      </TableCell>
                      <TableCell>
                        <Badge tone={STATUS_TONE[line.status]}>
                          {LINE_STATUS_LABEL[line.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-32 truncate">{line.row.floorName}</TableCell>
                      <TableCell className="max-w-36 truncate">{line.row.disciplineName}</TableCell>
                      <TableCell className="max-w-40 truncate">
                        {line.row.serviceName ?? (
                          <span className="italic text-(--color-content-muted)">
                            Frente inteira
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-56">
                        <span className="block truncate">{line.row.materialName}</span>
                        <span className="tabular text-xs text-(--color-content-muted)">
                          {line.row.materialCode}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular whitespace-nowrap">
                        {formatPurchaseQuantity(
                          line.row.quantity,
                          line.row.unit,
                          line.row.quantityProvisional,
                        )}
                      </TableCell>
                      <TableCell className="tabular whitespace-nowrap">
                        {formatBusinessDate(line.row.requiredDate)}
                      </TableCell>
                      <TableCell className="min-w-48 max-w-72 text-xs text-(--color-content-muted)">
                        {line.issues.map((issue) => issue.message).join(' ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
          {matching.length > VISIBLE_LINES && (
            <p className="text-xs text-(--color-content-muted)">
              Mostrando as primeiras {VISIBLE_LINES} de {plural(matching.length, 'linha', 'linhas')}{' '}
              deste filtro.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

// ── O que a leitura fez com a planilha ────────────────────────────────────────────────

function Leitura({ lines }: { readonly lines: LoadedLines }) {
  const skipped = summarizeSkipped(lines.skipped);

  return (
    <section aria-labelledby="conf-reading" className="flex flex-col gap-2">
      <h3 id="conf-reading" className="text-sm font-medium">
        O que a leitura fez com a planilha
      </h3>
      <ul className="flex flex-col gap-1 text-sm text-(--color-content-muted)">
        <li>
          <strong className="text-(--color-content)">
            {plural(lines.rows.length, 'linha', 'linhas')}
          </strong>{' '}
          vão para o sistema (material e terceirizado).
        </li>
        {lines.unions > 0 && (
          <li>
            {plural(lines.unions, 'linha', 'linhas')} da planilha se somaram a outra da mesma
            frente, serviço e material — as faces da fachada e o mesmo material de duas composições
            — e viraram {plural(lines.unionGroups, 'linha', 'linhas')} (RN-352). A observação de
            cada uma diz quantas se juntaram.
          </li>
        )}
        {skipped.notForSystem > 0 && (
          <li>
            Ficam só na planilha: {plural(skipped.notForSystem, 'linha marcada', 'linhas marcadas')}{' '}
            &ldquo;Não&rdquo; (equipamento, despesa indireta, seguro e risco).
          </li>
        )}
        {skipped.toConfirm > 0 && (
          <li>
            Não entram até serem confirmadas:{' '}
            {plural(skipped.toConfirm, 'linha marcada', 'linhas marcadas')} &ldquo;CONFIRMAR&rdquo;.
          </li>
        )}
      </ul>

      {lines.skipped.length > 0 && (
        <details className="text-xs text-(--color-content-muted)">
          <summary className="cursor-pointer">
            Ver as linhas que ficam de fora ({lines.skipped.length.toLocaleString('pt-BR')})
          </summary>
          <ul className="mt-1 max-h-32 overflow-y-auto">
            {lines.skipped.slice(0, 20).map((line) => (
              <li key={line.rowNumber}>
                Linha {line.rowNumber}: {line.reason}
              </li>
            ))}
            {lines.skipped.length > 20 && <li>… e mais {lines.skipped.length - 20}.</li>}
          </ul>
        </details>
      )}
    </section>
  );
}

// ── O que falta no cadastro ───────────────────────────────────────────────────────────

function Cadastro({
  preview,
  options,
  onOptions,
  canCreateServices,
  canCreateMaterials,
}: PurchaseConferenciaProps & { readonly preview: PurchaseLinesImportPreview }) {
  const { missing } = preview;
  const nothing =
    missing.floors.length === 0 &&
    missing.disciplines.length === 0 &&
    missing.services.length === 0 &&
    missing.materials.length === 0 &&
    missing.units.length === 0 &&
    missing.activities === 0;

  if (nothing) {
    return (
      <p className="text-sm text-(--color-content-muted)">
        O cadastro da obra já tem os pavimentos, as frentes, as disciplinas, os serviços, os
        materiais e as unidades que a planilha cita.
      </p>
    );
  }

  const provisionalMaterials = missing.materials.filter((material) =>
    isProvisionalMaterialCode(material.code),
  ).length;

  return (
    <fieldset className="flex flex-col gap-3 rounded-md border border-(--color-border-subtle) p-3">
      <legend className="px-1 text-sm font-medium">O que falta no cadastro da obra</legend>
      <p className="text-xs text-(--color-content-muted)">
        Nada é criado sem a sua escolha. Sem marcar, as linhas que dependem do que falta ficam de
        fora, com o motivo.
      </p>

      {missing.floors.length > 0 && (
        <Option
          id="opt-lines-floors"
          checked={options.createMissingFloors}
          onChange={(checked) => onOptions({ ...options, createMissingFloors: checked })}
          label={`Criar os pavimentos que faltam (${String(missing.floors.length)})`}
        >
          {listed(
            missing.floors.map(
              (floor) =>
                `${floor.floorName}${floor.level === null ? '' : ` (nível ${String(floor.level)})`}`,
            ),
          )}
        </Option>
      )}

      {missing.activities > 0 && (
        <Option
          id="opt-lines-activities"
          checked={options.createMissingActivities}
          onChange={(checked) => onOptions({ ...options, createMissingActivities: checked })}
          label={`Criar as frentes que faltam (${String(missing.activities)})`}
        >
          Uma frente é a disciplina num pavimento.
        </Option>
      )}

      {missing.disciplines.length > 0 && (
        <Option
          id="opt-lines-disciplines"
          checked={options.createMissingDisciplines}
          onChange={(checked) => onOptions({ ...options, createMissingDisciplines: checked })}
          label={`Criar as disciplinas novas (${String(missing.disciplines.length)})`}
        >
          {listed(missing.disciplines)} — entram no catálogo da obra, não no global.
        </Option>
      )}

      {missing.services.length > 0 && (
        <Option
          id="opt-lines-services"
          checked={options.createMissingServices}
          disabled={!canCreateServices}
          onChange={(checked) => onOptions({ ...options, createMissingServices: checked })}
          label={`Criar os serviços novos (${String(missing.services.length)})`}
        >
          {canCreateServices
            ? `${listed(
                missing.services.map(
                  (service) => `${service.disciplineName} · ${service.serviceName}`,
                ),
              )} — entram no catálogo da obra.`
            : 'Criar serviço é do cadastro do quantitativo, e o seu perfil não o administra.'}
        </Option>
      )}

      {missing.materials.length > 0 && (
        <Option
          id="opt-lines-materials"
          checked={options.createMissingMaterials}
          disabled={!canCreateMaterials}
          onChange={(checked) => onOptions({ ...options, createMissingMaterials: checked })}
          label={`Criar os materiais que faltam (${String(missing.materials.length)})`}
        >
          {canCreateMaterials
            ? `${listed(missing.materials.map((material) => `${material.code} ${material.name}`))}.${
                provisionalMaterials > 0
                  ? ` Os de código ORC-… (${String(provisionalMaterials)}) entram marcados como provisórios e não viram requisição até serem vinculados ao SKU (RN-347).`
                  : ''
              }`
            : 'Criar material é do catálogo global, que vale para todas as obras, e o seu perfil não o administra.'}
        </Option>
      )}

      {missing.units.length > 0 && (
        <p role="note" className="text-xs text-(--color-state-warn)">
          Unidades que o cadastro não conhece: {listed(missing.units)}. A importação não cria
          unidade: corrija a unidade dessas linhas na planilha, se for erro de digitação, e leia o
          arquivo de novo; se for unidade de verdade, peça a quem administra o sistema que a
          cadastre. Até lá, as linhas dessas unidades ficam de fora, com o motivo.
        </p>
      )}
    </fieldset>
  );
}
